import * as types from './mutations-types'

import conversationService from '../services'

import services from '@/services'

import Storage from '@octadesk-tech/storage'

import uuid from 'uuid'

import platform from 'platform'

import { messageStatus } from '@/helpers'
import { emitAppSession, getExternalSessionKey } from '@/services/iframe'

const stringHasLength = comment =>
  comment && comment.replace(/(\r\n\t|\n|\r\t)/gm, '').length > 0

export const createMessage = async (context, payload) => {
  const { comment, extendedData, quotedMessage } = payload || {}

  if (
    stringHasLength(comment) ||
    (context.state.message.attachments &&
      context.state.message.attachments.length)
  ) {
    const message = {
      key: uuid.v4(),
      user: context.getters.user,
      customFields: context.getters.customFields,
      quotedMessage,
      quotedMessageKey: (quotedMessage && quotedMessage.key) || undefined,
      comment: comment || '',
      extendedData: extendedData || {},
      type: 'public',
      time: new Date().toISOString(),
      domainFrom: context.getters.parentUrl,
      attachments: context.state.message.attachments,
      status: 'sending'
    }

    context.commit(types.CREATE_MESSAGE, message)

    return message
  }
}

export const closeConversation = async context => {
  services.iframe.postMessage({
    name: 'userClosedConversation'
  })

  const roomKey = context.getters.chatKey

  const closedRoom = await conversationService.closeConversation(
    roomKey,
    context.getters.user
  )

  services.session.removeSessionActive()

  context.commit(types.SET_CHAT, closedRoom)

  Storage.removeItem(`roomToken-${roomKey}`)

  Storage.removeItem(`room-${roomKey}`)

  await context.dispatch('disconnect')
}

export const showClosingChatLoader = (context, value) => {
  context.commit(types.SHOW_CLOSING_CHAT_LOADER, value)
}

export const updateChatCustomFields = ({ getters }) =>
  conversationService.updateChatCustomFields(
    getters.chatKey,
    getters.customFields
  )

export const updateChatOrganization = ({ getters, dispatch }) =>
  conversationService
    .updateChatOrganization(getters.chatKey, getters.organization)
    .then(organization => dispatch('setOrganization', organization))

export const updateMessages = context => {
  if (context.getters.chat) {
    services.session.removeSessionMessages(context.getters.chat.messages)

    const messages = services.session.getSessionMessages()

    context.dispatch('setMessages', messages)
  }
}

export const addAttachment = (context, attachment) =>
  context.commit(types.ADD_ATTACHMENT, attachment)

export const removeAttachment = (context, attachment) =>
  context.commit(types.REMOVE_ATTACHMENT, attachment)

export const clearAttachments = context =>
  context.commit(types.CLEAR_ATTACHMENTS)

export const retrySendMessage = async (context, oldMessage) => {
  context.commit(types.SET_MESSAGE_SENDING, oldMessage)

  return conversationService
    .sendMessage(context.getters.chatKey, oldMessage)
    .then(m => context.commit(types.SET_MESSAGE_SENDED, m))
    .catch(() => context.commit(types.SET_MESSAGE_ERROR, oldMessage))
}

export const sendMessage = async (context, payload) => {
  const message = await context.dispatch('createMessage', payload)

  if (message) {
    if (!context.getters.connected) {
      context.dispatch('connect')
    }

    return conversationService
      .sendMessage(context.getters.chatKey, message)
      .then(m => context.commit(types.SET_MESSAGE_SENDED, m))
      .catch(() => context.commit(types.SET_MESSAGE_ERROR, message))
  }
}

export const setMessages = (context, messages) =>
  context.commit(types.SET_MESSAGES, messages)

function getEnvironmenteInformations() {
  return {
    os: platform.os.family.split(' ')[0] + ' ' + platform.os.version,
    device: platform.product,
    browser: platform.name,
    language: window.navigator.language
  }
}

export const createChat = async (context, payload = {}) => {
  context.commit(types.SET_CREATING_CHAT, true)

  const payloadCustomFields = payload.customFields || []

  const currentCustomFields = context.getters.customFields || []

  const customFields = [...payloadCustomFields, ...currentCustomFields]

  const { closed, organization, flux, user, parentUrl } = context.getters

  const messages = payload.comment
    ? [await createMessage(context, { comment: payload.comment })]
    : []

  const { room, token } = await conversationService.createChat({
    status: payload.status || context.getters.options.status || undefined,
    messages,
    closed,
    domainFrom: parentUrl,
    organization,
    flux,
    user,
    customFields,
    widgetEnvironmentInformation: getEnvironmenteInformations()
  })

  context.dispatch('setWidgetRoomToken', { token, roomKey: room.key })

  await context.dispatch('setChat', room)

  if (room.flux && flux && room.flux.id !== flux.id) {
    await context.dispatch('setFlux', room.flux)
  }

  context.commit(types.SET_CREATING_CHAT, false)

  services.iframe.postMessage({
    name: 'onCreateChat',
    chat: room
  })

  if (context.getters.customFields && context.getters.customFields.length) {
    await context.dispatch('updateChatCustomFields')
  }

  await context.dispatch('startFluxBot')
}

export const setChat = (context, chat) => {
  if (chat) {
    context.dispatch('updateChat', { chat, type: 'set' })

    const sessionKey = getExternalSessionKey('chatKey')

    if (sessionKey) {
      emitAppSession({
        [sessionKey]: chat.key
      })
    }

    context.dispatch('clearSessionPages')

    context.dispatch('setSessionChatKey')

    context.dispatch('readMessages')

    context.dispatch('setOrganization', chat.organization)

    if (
      chat.flux &&
      (!context.getters.flux || context.getters.flux.id !== chat.flux.id)
    ) {
      context.dispatch('getFlux', chat.flux.id)
    }

    if (chat.survey && chat.survey.responseTime) {
      context.dispatch('setSurveyFinished', true)
    }
  } else {
    console.error('Error on setChat action: missing chat data')
  }
}

export const setWidgetRoomToken = (context, { token, roomKey }) => {
  context.commit(types.SET_TOKEN, token)

  Storage.setItem(`roomToken-${roomKey}`, token)
}

export const updateChat = (context, { chat, type }) => {
  const actionType = type === 'set' ? types.SET_CHAT : types.UPDATE_CHAT

  context.commit(actionType, chat)

  Storage.setItem(`room-${chat.key}`, chat)
}

let startingFluxBot = false

export const startFluxBot = async context => {
  if (startingFluxBot) return

  startingFluxBot = true

  const { flux, active, connected, chat } = context.getters

  if (flux && flux.botId && active) {
    if (!chat) {
      await context.dispatch('setGuestUser')

      startingFluxBot = false

      await context.dispatch('goToConversation')

      return
    } else {
      if (!connected) {
        await context.dispatch('connect')
      }

      if (!chat.messages || !chat.messages.length) {
        await conversationService.startFluxBot(chat.key)
      }
    }
  }

  startingFluxBot = false
}

export const readMessages = context => {
  const { active, chat, unrededMessages, reading, user } = context.getters

  if (active && chat && unrededMessages && !reading) {
    context.commit(types.SET_READING_MESSAGES, true)

    conversationService
      .readMessages(chat.key, user)
      .then(() => context.commit(types.SET_READING_MESSAGES, false))
      .catch(() => context.commit(types.SET_READING_MESSAGES, false))
  }
}

export const receiveMessages = context => {
  if (context.getters.chat) {
    const receivedMessages = context.getters.chat.messages.filter(
      m =>
        m.status === messageStatus.sended &&
        m.user.id !== context.getters.user.id
    )

    const actions = receivedMessages.map(m =>
      conversationService.receiveMessage(context.getters.chat.key, m.key)
    )

    return Promise.all(actions)
  }
}

export const setTyping = (context, type) =>
  conversationService.setTyping(
    context.getters.chatKey,
    type,
    context.getters.user
  )

export const setScrollBottom = (context, value) =>
  context.commit(types.SCROLL_BOTTOM, value)

export const focusMessage = (context, msgKey) =>
  context.commit(types.FOCUS_MESSAGE, msgKey)

export const clearFocusMessage = context =>
  context.commit(types.CLEAR_FOCUS_MESSAGE)

export const newConversation = async context => {
  context.commit(types.NEW_CONVERSATION)

  if (context.getters.forceSelectFields) {
    await context.dispatch('setCustomFields', [])
  }

  await context.dispatch('setMessages', [])

  await context.dispatch('setFlux')

  await context.dispatch('clearSessionMessages')

  await context.dispatch('clearCachedUser')

  await context.dispatch('setSessionChatKey')

  await context.dispatch('disconnect')
}

export const includeChatArticle = async (context, article) => {
  if (context.getters.chatKey) {
    services.room.includeChatArticle(context.getters.chatKey, article)
  } else {
    await context.dispatch('createChat')

    services.room.includeChatArticle(context.getters.chatKey, article)
  }
}

let activityTimeout

export const updateActivity = context => {
  const inactivityTimeoutEnabled =
    context.getters.settings.conversation.inactivityTimeout.enabled

  const inactivityTimeoutDelay =
    context.getters.settings.conversation.inactivityTimeout.delay

  if (inactivityTimeoutEnabled) {
    if (activityTimeout) {
      clearTimeout(activityTimeout)
    }

    activityTimeout = setTimeout(() => {
      context.dispatch('updateConversation')
    }, 1000 * 60 * inactivityTimeoutDelay)
  }
}

export const sendSurvey = async (context, survey) => {
  const updatedChat = await services.room.surveyResponse(
    context.getters.chatKey,
    survey
  )

  context.dispatch('updateChat', { chat: updatedChat })
}

export const sendComment = async (context, comment) => {
  const updatedChatWithComments = await services.room.surveyComment(
    context.getters.chatKey,
    comment
  )

  context.dispatch('updateChat', { chat: updatedChatWithComments })
}

export const surveyOpen = context =>
  services.room.surveyOpened(context.getters.chatKey)

export const enableEmojiPage = context => {
  context.commit(types.SET_EMOJI_PAGE_STATUS, true)
}

export const disableEmojiPage = context => {
  context.commit(types.SET_EMOJI_PAGE_STATUS, false)
}

export const updateComment = (context, value) => {
  context.commit(types.UPDATE_COMMENT, value)
}

export const updateCommentErrorMaxLength = (context, value) => {
  context.commit(types.UPDATE_COMMENT_ERROR_MAX_LENGTH, value)
}

export const appendTextToComment = (context, value) => {
  let { comment } = context.getters

  context.commit(types.UPDATE_COMMENT, (comment || '') + value)
}

export const quoteMessage = (context, message) => {
  context.commit(types.QUOTE_MESSAGE, message)
}

export const cancelQuote = context => context.commit(types.CANCEL_QUOTE_MESSAGE)

export const setShowKbCard = ({ commit }, show) =>
  commit(types.SET_SHOW_KB_CARD, show)

export const setVoiceRecording = ({ commit }, recording) =>
  commit(types.SET_VOICE_RECORDING, recording)

export const setFileDragging = ({ commit }, dragging) =>
  commit(types.SET_FILE_DRAGGING, dragging)

export const toggleNumberInput = ({ commit }) =>
  commit(types.TOGGLE_NUMBER_INPUT)

export const setShowNumberInput = ({ commit }, show) =>
  commit(types.SHOW_NUMBER_INPUT, show)

export const setNumberInputValid = ({ commit }, valid) =>
  commit(types.SET_NUMBER_INPUT_VALID, valid)

export const setShowDatePicker = ({ commit }, show) =>
  commit(types.SHOW_DATE_PICKER, show)

export const toggleDatePickerValid = ({ commit }, valid) =>
  commit(types.TOOGLE_DATE_PICKER_VALID, valid)

export const handleReopen = (context, room) => {
  const lastMessage = room.messages[room.messages.length - 1]

  if (
    context.getters.options &&
    context.getters.options.openOnMessage === 'true' &&
    lastMessage &&
    lastMessage.user.type === 1 &&
    lastMessage.type === 'public' &&
    !lastMessage.clientRead
  ) {
    services.iframe.notifyAgentMessage()
  }
}

export const setUploadInProcess = ({ commit }, hasUploadInProcess) =>
  commit(types.SET_UPLOAD_IN_PROCESS, hasUploadInProcess)
